<template>
  <section
    :class="{
      error: validationErrors.emptyParty,
    }"
  >
    <div class="row">
      <label class="adults-count">
        <translate tag="em" :tt="{ adultsCount: adults }"
          >{adultsCount} adults (18+)</translate
        >
      </label>

      <number-stepper
        v-model="adults"
        data-test="search-wizard-adults-number"
        :min="0"
        :max="30"
        :default-value="adults"
        @input="changeGuests"
      />
    </div>

    <div class="row">
      <label
        ><translate :tt="{ childrenCount: children }"
          >{childrenCount} children</translate
        ></label
      >
      <number-stepper
        v-model="children"
        data-test="search-wizard-children-number"
        :min="0"
        :max="30"
        :default-value="children"
        @input="changeGuests"
      />
    </div>

    <div v-if="children" class="children">
      <div class="row">
        <translate tag="label" :class="{ error: validationErrors.childAges }">
          Age of children
        </translate>
        <div class="child-ages">
          <template v-for="childIndex in children" :key="childIndex">
            <div
              class="ui-select"
              :class="{
                error:
                  validationErrors.childAges && !doesChildAgeExist(childIndex),
              }"
            >
              <span class="caret"><span class="l" /> <span class="r" /></span>
              <select
                v-model="childAges[childIndex - 1]"
                data-test="search-wizard-child-ages"
                @change="changeGuests"
              >
                <option value="" selected disabled>---</option>
                <translate
                  v-for="childAge in minAdultAge"
                  :key="childAge"
                  tag="option"
                  :value="childAge"
                  :tt="{ childAge }"
                >
                  {childAge} years old
                </translate>
              </select>
            </div>
          </template>
        </div>
      </div>
    </div>
    <transition name="fade" tag="div">
      <translate
        v-if="validationErrors.emptyParty"
        tag="div"
        class="input-error-msg"
      >
        Please have at least one person in your party.
      </translate>
      <translate
        v-else-if="validationErrors.childAges"
        tag="div"
        class="input-error-msg"
      >
        Please enter valid ages for all the children in your party.
      </translate>
    </transition>

    <div class="row">
      <label class="dogs-count">
        <translate :tt="{ dogs }"> {dogs} dogs </translate>
      </label>

      <number-stepper
        v-model="dogs"
        data-test="search-wizard-dogs-number"
        :min="0"
        :max="30"
        :default-value="dogs"
        @input="changeGuests"
      />
    </div>
    <group-booking />
    <footer class="guests-form__footer">
      <translate
        tag="button"
        type="button"
        class="button minimal sml"
        @click="closeForm"
      >
        Cancel
      </translate>
      <translate
        tag="button"
        type="button"
        :disabled="!isValid"
        class="button primary asblock"
        @click="emitPartyChange"
      >
        Apply
      </translate>
    </footer>
    <nuxt-to-meta :nuxt2meta="head" />
  </section>
</template>

<script lang="ts">
import type {
  GuestsFormErrors,
  GuestsFormParty,
} from '~/components/forms/guests-form/schemas'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

@Component({
  emits: ['partyChanged', 'form-closed'],
})
class GuestsForm extends Vue {
  @Prop({ required: true })
  guests: GuestsFormParty

  @Prop({ required: true })
  pitchtypeCapacity: number

  get head() {
    return {
      bodyAttrs: {
        class: this.$isDesktop ? '' : 'noscroll',
      },
    }
  }

  validationErrors: GuestsFormErrors = {
    emptyParty: false,
    childAges: false,
  }

  adults: number = 2
  children: number = 0
  childAges: number[] = []
  dogs: number = 0

  minAdultAge = [...Array(19).keys()]

  beforeMount() {
    this.adults = this.guests.adults
    this.children = this.guests.childAges.length
    this.childAges = [...this.guests.childAges]
    this.dogs = this.guests.dogs || 0
  }

  isChildAgesValid(): boolean {
    return (
      this.childAges.slice(0, this.children).filter((a) => a >= 0).length ===
      this.children
    )
  }

  isPartyWithPeople(): boolean {
    return this.adults !== 0 || this.children !== 0
  }

  doesChildAgeExist(childIndex: number) {
    return this.childAges[childIndex - 1] >= 0
  }

  validateGuestsModel() {
    this.clearValidationErrors()
    this.validationErrors.emptyParty = !this.isPartyWithPeople()
    if (this.childAges) {
      this.validationErrors.childAges = !this.isChildAgesValid()
    }
  }

  clearValidationErrors() {
    this.validationErrors = {
      emptyParty: false,
      childAges: false,
    }
  }

  changeGuests() {
    if (!this.isValid) {
      this.validateGuestsModel()
    }
  }

  closeForm() {
    this.clearValidationErrors()
    this.$emit('form-closed')
  }

  emitPartyChange() {
    this.validateGuestsModel()
    if (this.isValid) {
      this.$emit('partyChanged', this.sanitizedGuests)
    }
  }

  get isValid() {
    return !this.validationErrors.emptyParty && !this.validationErrors.childAges
  }

  get sanitizedGuests(): GuestsFormParty {
    const childAges =
      this.childAges.length > this.children
        ? this.childAges.slice(0, this.children)
        : this.childAges
    return {
      adults: this.adults,
      childAges,
      children: this.children,
      dogs: this.dogs,
    }
  }
}

export default toNative(GuestsForm)
</script>
<style scoped>
section {
  margin: 1em 0.5em 1.5em;
}

label {
  font-size: inherit;
}

.adults-count {
  font-weight: bold;
}

.adults-count :deep(span) {
  color: var(--colour-text-light);
  font-weight: normal;
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 1em 0;
  padding: 0 0 1em 0;
  border-bottom: 1px solid var(--colour-table-borders);
}

.children .row {
  align-items: flex-start;
  border-bottom: none;
  margin: 0 0;
  padding: 0 0;
}

.children {
  margin: 0 0 1em 0;
  padding: 0 0 1em 0;
  border-bottom: 1px solid var(--colour-table-borders);
}

.children label {
  margin-top: 0.35em;
}

.child-ages {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
}

.child-ages select {
  font-size: 1em;
}

.ui-select {
  flex-shrink: 0;
  margin: 0 0 0.75em 0;
}

.guests-form__footer {
  display: flex;
  justify-content: space-evenly;
}
</style>
